import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CEPRepository } from '../repositories/cep.repository';
import { Endereco, ParametrosBuscaEndereco, RespostaViaCEP, ErroViaCEP } from '../models/endereco.model';

/**
 * Serviço que implementa o repositório de CEP
 * Comunica com a API do ViaCEP para consultar endereços
 */
@Injectable({
  providedIn: 'root'
})
export class CEPService implements CEPRepository {

  private readonly BASE_URL = `${environment.FI_FINANC_IMOB_BE}endereco`;

  constructor(private http: HttpClient) {}

  /**
   * Consulta um endereço pelo CEP
   * @param cep CEP no formato de 8 dígitos
   * @returns Observable com os dados do endereço ou erro
   */
  consultarPorCEP(cep: string): Observable<RespostaViaCEP> {
    // Validação básica do formato do CEP
    if (!this.validarFormatoCEP(cep)) {
      return throwError(() => new Error('CEP deve conter exatamente 8 dígitos numéricos'));
    }

    const url = `${this.BASE_URL}/${cep}`;
    
    return this.http.get<RespostaViaCEP>(url).pipe(
      map(response => {
        // Verifica se a resposta indica erro (CEP não encontrado)
        if ('erro' in response && response.erro === true) {
          return { erro: true } as ErroViaCEP;
        }
        return response as Endereco;
      }),
      catchError(error => {
        console.error('Erro ao consultar CEP no backend');
        return throwError(() => new Error('Erro ao consultar CEP. Tente novamente.'));
      })
    );
  }

  /**
   * Busca CEPs por endereço (UF, cidade e logradouro)
   * @param parametros Parâmetros da busca
   * @returns Observable com array de endereços encontrados
   */
  buscarPorEndereco(parametros: ParametrosBuscaEndereco): Observable<Endereco[]> {
    // Validação dos parâmetros
    if (!this.validarParametrosBusca(parametros)) {
      return throwError(() => new Error('UF, cidade e logradouro são obrigatórios. Cidade e logradouro devem ter pelo menos 3 caracteres.'));
    }

    const { uf, cidade, logradouro } = parametros;
    const query = new URLSearchParams({
      uf,
      cidade,
      logradouro
    }).toString();
    const url = `${this.BASE_URL}/buscar?${query}`;
    
    return this.http.get<Endereco[]>(url).pipe(
      catchError(error => {
        console.error('Erro ao buscar por endereço no backend');
        return throwError(() => new Error('Erro ao buscar endereço. Tente novamente.'));
      })
    );
  }

  /**
   * Valida o formato do CEP
   * @param cep CEP a ser validado
   * @returns true se o formato é válido
   */
  private validarFormatoCEP(cep: string): boolean {
    // Remove espaços e caracteres especiais
    const cepLimpo = cep.replace(/\D/g, '');
    // Verifica se tem exatamente 8 dígitos
    return /^\d{8}$/.test(cepLimpo);
  }

  /**
   * Valida os parâmetros de busca por endereço
   * @param parametros Parâmetros a serem validados
   * @returns true se os parâmetros são válidos
   */
  private validarParametrosBusca(parametros: ParametrosBuscaEndereco): boolean {
    const { uf, cidade, logradouro } = parametros;
    
    return !!uf && 
           !!cidade && 
           !!logradouro && 
           cidade.trim().length >= 3 && 
           logradouro.trim().length >= 3;
  }
}
